import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Input, Label, Message, Table, Loader, Confirm } from 'semantic-ui-react'
import api from '../../../Service/Api'
import backend from '../../../Service/Backend'

type Props = { merchantAppId: number | string }

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`
// backend.save/fetch throw { errMessage, errCode, data } for API errors (not an axios error)
const errorText = (e: any) => e?.errMessage || e?.response?.data?.message || e?.message || 'Something went wrong.'

const StripeCheckoutPanel = ({ merchantAppId }: Props) => {
  const [panel, setPanel] = useState<any>(null)
  const [orders, setOrders] = useState<any[]>([])
  const [moreOrders, setMoreOrders] = useState(false)
  const [busy, setBusy] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [feePercent, setFeePercent] = useState('0')
  const [feeFixed, setFeeFixed] = useState('0.00')
  const [website, setWebsite] = useState('')
  const [editing, setEditing] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  const applyPanel = (p: any) => {
    setPanel(p)
    const m = p?.merchant
    setFeePercent(m ? String(m.fee_percent) : '0')
    setFeeFixed(m ? (m.fee_fixed_cents / 100).toFixed(2) : '0.00')
    setWebsite(m?.website_url || '')
  }

  const loadOrders = useCallback(async (before?: number) => {
    const rows: any[] = await backend.fetch({ api: api.stripeCheckout.orders, queryParam: { merchant_app_id: merchantAppId, ...(before ? { before } : {}) } }) || []
    setOrders(prev => before ? [...prev, ...rows] : rows)
    setMoreOrders(rows.length === 20)
  }, [merchantAppId])

  const load = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const p = await backend.fetch({ api: api.stripeCheckout.getApp, queryParam: { merchant_app_id: merchantAppId } })
      applyPanel(p)
      if (p?.merchant) await loadOrders()
    } catch (e) { setError(errorText(e)) } finally { setBusy(false) }
  }, [merchantAppId, loadOrders])

  useEffect(() => { load() }, [load])

  const run = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true); setError(''); setNotice('')
    try {
      const out = await fn()
      if (out && out.app) applyPanel(out)
      if (ok) setNotice(ok)
    } catch (e) { setError(errorText(e)) } finally { setBusy(false) }
  }

  const settingsPayload = () => ({
    merchant_app_id: merchantAppId,
    fee_percent: feePercent === '' ? 0 : Number(feePercent),
    fee_fixed_cents: feeFixed === '' ? 0 : Math.round(Number(feeFixed) * 100),
    website_url: website.trim(),
  })

  const loadMore = async () => {
    if (loadingMore || !orders.length) return
    setLoadingMore(true)
    try { await loadOrders(orders[orders.length - 1].id) } catch (e) { setError(errorText(e)) } finally { setLoadingMore(false) }
  }

  const copy = (text: string, what: string) => {
    navigator.clipboard.writeText(text).then(() => setNotice(`${what} copied.`), () => setError('Could not copy. Select the text and copy it.'))
  }

  if (!panel) return busy ? <Loader active inline='centered' /> : (error ? <Message negative content={error} /> : null)
  const m = panel.merchant

  const feeFields = (
    <Form.Group widths='equal'>
      <Form.Field><label>Trinity fee (%)</label><Input type='number' min='0' max='20' step='0.01' value={feePercent} onChange={(e) => setFeePercent(e.target.value)} /></Form.Field>
      <Form.Field><label>Trinity fee ($ per payment)</label><Input type='number' min='0' max='10' step='0.01' value={feeFixed} onChange={(e) => setFeeFixed(e.target.value)} /></Form.Field>
      <Form.Field><label>Merchant website (optional)</label><Input placeholder='https://…' value={website} onChange={(e) => setWebsite(e.target.value)} /></Form.Field>
    </Form.Group>
  )

  return (
    <div className='stripe-checkout-panel'>
      {error && <Message negative content={error} />}
      {notice && <Message positive content={notice} />}
      {busy && <Loader active inline='centered' size='small' />}

      {!m && (
        panel.app.eligible ? (
          <Form>
            <p>Let this merchant sell on their website through their own Stripe account. The merchant connects (or creates) their Stripe account with a link from here.</p>
            {feeFields}
            <Button primary disabled={busy} onClick={() => run(() => backend.save({ api: api.stripeCheckout.setup, payLoad: settingsPayload() }))}>Set up Stripe Checkout</Button>
          </Form>
        ) : <Message info content="Withdrawn or deleted applications can't use Stripe Checkout." />
      )}

      {m && m.status !== 'connected' && (
        <>
          <p>
            <Label color={m.status === 'disconnected' ? 'red' : 'grey'}>{m.status === 'disconnected' ? 'Disconnected' : 'Not connected'}</Label>{' '}
            {m.status === 'disconnected' ? `Disconnected${m.disconnected_ts ? ` on ${new Date(m.disconnected_ts).toLocaleString()}` : ''} (by staff or by the merchant in Stripe). Send a new link to reconnect.` : 'Send this link to the merchant. They sign in to Stripe (or create an account) and approve Trinity. The link is valid for 24 hours.'}
          </p>
          <Input fluid readOnly value={panel.connectLink || ''} action={{ content: 'Copy', onClick: () => copy(panel.connectLink, 'Link') }} />
          <div style={{ marginTop: 10 }}>
            <Button disabled={busy || !panel.app.merchantEmail} onClick={() => run(async () => {
              const r = await backend.save({ api: api.stripeCheckout.emailLink, payLoad: { merchant_app_id: merchantAppId } })
              return r
            }, `Link emailed to ${panel.app.merchantEmail}.`)}>Email link to merchant</Button>
            <Button basic disabled={busy} onClick={load}>New link</Button>
          </div>
        </>
      )}

      {m && m.status === 'connected' && (
        <>
          <p>
            <Label color={m.charges_enabled ? 'green' : 'orange'}>{m.charges_enabled ? 'Taking payments' : 'Setup unfinished in Stripe'}</Label>{' '}
            Stripe account <code>{m.stripe_account_id}</code>
          </p>
          {!editing ? (
            <p>Trinity fee: {Number(m.fee_percent)}% + {money(m.fee_fixed_cents)} {m.website_url ? <>· Website: {m.website_url}</> : null} <Button size='mini' basic onClick={() => setEditing(true)}>Edit</Button></p>
          ) : (
            <Form>
              {feeFields}
              <Button primary size='small' disabled={busy} onClick={() => run(async () => { const r = await backend.save({ api: api.stripeCheckout.updateSettings, payLoad: settingsPayload() }); setEditing(false); return r }, 'Saved.')}>Save</Button>
              <Button size='small' basic onClick={() => { setEditing(false); applyPanel(panel) }}>Cancel</Button>
            </Form>
          )}
          <p><b>Products in their Stripe (live):</b>{' '}
            {panel.productsError ? panel.productsError
              : (panel.products && panel.products.length ? panel.products.map((p: any) => `${p.name} ${money(p.unitAmountCents)}`).join(' · ') : 'No products yet. The merchant adds them in their Stripe Dashboard.')}
          </p>
          {panel.snippet && (
            <>
              <p><b>Website code</b> (the merchant pastes this once)</p>
              <pre style={{ background: '#1b1f24', color: '#e6e6e6', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{panel.snippet}</pre>
              <Button size='small' onClick={() => copy(panel.snippet, 'Website code')}>Copy website code</Button>
            </>
          )}
          <div style={{ marginTop: 10 }}>
            <Button size='small' basic disabled={busy} onClick={() => run(() => backend.save({ api: api.stripeCheckout.refresh, payLoad: { merchant_app_id: merchantAppId } }), 'Status refreshed.')}>Refresh</Button>
            <Button size='small' color='red' basic disabled={busy} onClick={() => setConfirmDisconnect(true)}>Disconnect</Button>
          </div>
          <Confirm
            open={confirmDisconnect}
            content='Disconnect this merchant? Their website stops taking orders until they connect again.'
            confirmButton='Disconnect'
            onCancel={() => setConfirmDisconnect(false)}
            onConfirm={() => { setConfirmDisconnect(false); run(() => backend.save({ api: api.stripeCheckout.disconnect, payLoad: { merchant_app_id: merchantAppId } }), 'Disconnected.') }}
          />
        </>
      )}

      {m && (
        <>
          <h4 style={{ marginTop: 20 }}>Orders</h4>
          {orders.length === 0 ? <p>No orders yet.</p> : (
            <Table compact size='small'>
              <Table.Header><Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell><Table.HeaderCell>Buyer</Table.HeaderCell><Table.HeaderCell>Items</Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>Total</Table.HeaderCell><Table.HeaderCell textAlign='right'>Fee</Table.HeaderCell><Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row></Table.Header>
              <Table.Body>
                {orders.map((o: any) => (
                  <Table.Row key={o.id}>
                    <Table.Cell>{new Date(o.created_ts).toLocaleString()}</Table.Cell>
                    <Table.Cell>{o.customer_email || '—'}</Table.Cell>
                    <Table.Cell>{(o.line_items || []).map((l: any) => `${l.name} ×${l.quantity}`).join(', ')}</Table.Cell>
                    <Table.Cell textAlign='right'>{money(o.amount_total_cents)}</Table.Cell>
                    <Table.Cell textAlign='right'>{['paid', 'partially_refunded', 'refunded'].includes(o.status) ? money(o.fee_cents) : '—'}</Table.Cell>
                    <Table.Cell>{o.status.replace('_', ' ')}{o.refunded_cents ? ` (${money(o.refunded_cents)})` : ''}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
          {moreOrders && <Button size='small' basic loading={loadingMore} disabled={busy || loadingMore} onClick={loadMore}>Load more</Button>}
          <p style={{ color: '#666', fontSize: 12 }}>Refunds are made by the merchant in their Stripe Dashboard and show up here automatically.</p>
        </>
      )}
    </div>
  )
}

export default StripeCheckoutPanel
